import React, { useState, useEffect } from 'react';
import { X, User, Building, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

interface Property {
  id: string;
  listing_id: string;
  title: string;
  address: string;
  list_price: number;
}

interface Corretor {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface TransferPropertiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userToDelete: {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  onConfirmDelete: () => void;
}

export const TransferPropertiesModal: React.FC<TransferPropertiesModalProps> = ({
  isOpen,
  onClose,
  userToDelete,
  onConfirmDelete
}) => {
  console.log('🔧 TransferPropertiesModal renderizado:', { isOpen, userToDelete });
  
  const [properties, setProperties] = useState<Property[]>([]);
  const [corretores, setCorretores] = useState<Corretor[]>([]);
  const [selectedCorretores, setSelectedCorretores] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && userToDelete) {
      loadUserProperties();
      loadAvailableCorretores();
    }
  }, [isOpen, userToDelete]);

  const loadUserProperties = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('properties')
        .select('id, listing_id, title, address, list_price')
        .or(`user_id.eq.${userToDelete.user_id},responsible_broker_id.eq.${userToDelete.user_id}`);

      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os imóveis do usuário",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCorretores = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email, role')
        .in('role', ['corretor', 'gerente', 'diretor'])
        .eq('is_active', true)
        .neq('user_id', userToDelete.user_id);

      if (error) throw error;
      const list = (data || []).filter(c => c.user_id) as Corretor[];
      console.log('👥 Usuários aptos a receber imóveis:', list.length, list);
      setCorretores(list);

      if (list.length === 0) {
        toast({
          title: "Nenhum usuário disponível",
          description: "Não há outros usuários ativos para receber os imóveis. Crie um novo usuário antes de excluir este.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Erro ao carregar corretores:', error);
      toast({
        title: "Erro", 
        description: "Não foi possível carregar os usuários disponíveis",
        variant: "destructive",
      });
    }
  };

  const handleCorretorToggle = (corretorId: string) => {
    setSelectedCorretores(prev => 
      prev.includes(corretorId)
        ? prev.filter(id => id !== corretorId)
        : [...prev, corretorId]
    );
  };

  const distributeProperties = (properties: Property[], corretores: string[]) => {
    if (corretores.length === 0) return [];

    const distribution: { [key: string]: Property[] } = {};
    corretores.forEach(corretor => {
      distribution[corretor] = [];
    });

    // Distribuir de forma equilibrada
    properties.forEach((property, index) => {
      const corretorIndex = index % corretores.length;
      const corretor = corretores[corretorIndex];
      distribution[corretor].push(property);
    });

    return distribution;
  };

  const handleTransferAndDelete = async () => {
    if (selectedCorretores.length === 0) {
      toast({
        title: "Erro",
        description: "Selecione pelo menos um corretor para transferir os imóveis",
        variant: "destructive",
      });
      return;
    }

    try {
      setTransferring(true);

      // Buscar TODAS as properties (ativas e inativas) onde o usuário é user_id OU responsible_broker_id
      const { data: allProps, error: allPropsError } = await supabase
        .from('properties')
        .select('id, user_id, responsible_broker_id')
        .or(`user_id.eq.${userToDelete.user_id},responsible_broker_id.eq.${userToDelete.user_id}`);

      if (allPropsError) throw allPropsError;

      const propsAsUserId = (allProps || []).filter(p => p.user_id === userToDelete.user_id);
      const propsAsBroker = (allProps || []).filter(p => p.responsible_broker_id === userToDelete.user_id);

      // Distribuir properties por user_id
      const distUserId = distributeProperties(propsAsUserId as any, selectedCorretores);
      for (const [corretorId, propsToTransfer] of Object.entries(distUserId)) {
        if ((propsToTransfer as any).length > 0) {
          const ids = (propsToTransfer as any).map((p: any) => p.id);
          const { error } = await supabase
            .from('properties')
            .update({ user_id: corretorId })
            .in('id', ids);
          if (error) throw error;
        }
      }

      // Distribuir responsible_broker_id
      const distBroker = distributeProperties(propsAsBroker as any, selectedCorretores);
      for (const [corretorId, propsToTransfer] of Object.entries(distBroker)) {
        if ((propsToTransfer as any).length > 0) {
          const ids = (propsToTransfer as any).map((p: any) => p.id);
          const { error } = await supabase
            .from('properties')
            .update({ responsible_broker_id: corretorId })
            .in('id', ids);
          if (error) throw error;
        }
      }

      // Transferir leads relacionados (assigned_to)
      const { error: leadsErr } = await supabase
        .from('leads')
        .update({ assigned_to: selectedCorretores[0] })
        .eq('assigned_to', userToDelete.user_id);
      if (leadsErr) console.error('Erro ao transferir leads:', leadsErr);

      // Remover notificações do usuário (não faz sentido transferir)
      const { error: notifErr } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userToDelete.user_id);
      if (notifErr) console.error('Erro ao remover notificações:', notifErr);

      // Resumo
      const summary = selectedCorretores.map(corretorId => {
        const corretor = corretores.find(c => c.user_id === corretorId);
        const count = ((distUserId as any)[corretorId]?.length || 0) + ((distBroker as any)[corretorId]?.length || 0);
        return `${corretor?.first_name} ${corretor?.last_name}: ${count} registros`;
      }).join('\n');

      toast({
        title: "Transferência realizada com sucesso",
        description: `Registros transferidos:\n${summary}`,
      });

      onConfirmDelete();
      onClose();

    } catch (error: any) {
      console.error('Erro na transferência:', error);
      toast({
        title: "Erro na transferência",
        description: error?.message || "Não foi possível transferir todos os imóveis. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setTransferring(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            <h2 className="text-xl font-bold text-gray-800">
              Transferir Imóveis Antes da Exclusão
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando dados...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Aviso */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-orange-800">
                    Usuário possui imóveis captados
                  </h3>
                  <p className="text-orange-700 mt-1">
                    O usuário <strong>{userToDelete.first_name} {userToDelete.last_name}</strong> possui{' '}
                    <strong>{properties.length}</strong> imóvel(is) ativo(s). 
                    É necessário transferir estes imóveis para outros corretores antes da exclusão.
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de Imóveis */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Building className="h-5 w-5" />
                Imóveis que serão transferidos ({properties.length})
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                {properties.map((property) => (
                  <div key={property.id} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                    <div>
                      <p className="font-medium text-gray-800">{property.title}</p>
                      <p className="text-sm text-gray-600">{property.listing_id} - {property.address}</p>
                    </div>
                    <p className="font-semibold text-green-600">{formatPrice(property.list_price)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Seleção de Corretores */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="h-5 w-5" />
                Selecionar corretores para receber os imóveis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                {corretores.map((corretor) => (
                  <div
                    key={corretor.user_id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedCorretores.includes(corretor.user_id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => handleCorretorToggle(corretor.user_id)}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedCorretores.includes(corretor.user_id)}
                        onChange={() => {}}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div>
                        <p className="font-medium text-gray-800">
                          {corretor.first_name} {corretor.last_name}
                        </p>
                        <p className="text-sm text-gray-600">{corretor.email}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {selectedCorretores.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Distribuição prevista:</h4>
                  {selectedCorretores.map((corretorId) => {
                    const corretor = corretores.find(c => c.user_id === corretorId);
                    const distribution = distributeProperties(properties, selectedCorretores);
                    const count = (distribution as any)[corretorId]?.length || 0;
                    
                    return (
                      <div key={corretorId} className="flex justify-between text-sm text-blue-700">
                        <span>{corretor?.first_name} {corretor?.last_name}</span>
                        <span>{count} imóveis</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="border-t pt-6">
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleTransferAndDelete}
                  disabled={selectedCorretores.length === 0 || transferring}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {transferring ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Transferindo...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Transferir e Excluir Usuário
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};